import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import type { FooterLink as ServiceFooterLink } from '@amzn/mulberry-internal-typescript-client';
import { LoadingSpinner, LoadingSpinnerContainer, ErrorBox, CustomPageContent } from '@amzn/burberry-ui-components';
import { UH_OH, GENERIC_ERROR_STRING, PAGE_TITLE_TERMS, PAGE_TITLE_PRIVACY_POLICY, PAGE_TITLE_FAQ } from '@amzn/d2c-application-website-common';
import { selectManifest, selectTemplate } from 'src/store/templateSlice';
import { getBaseUrl, CUSTOM_PAGE_MANIFEST_KEY, getIsPreview } from 'src/utils/siteUtils';
import { metricEmitter } from 'src/utils/metricEmitter';
import { awsRum } from 'src/rum';

export type CustomPageType = 'faq' | 'tos' | 'pp';

const FOOTER_LINK_TYPE_FOR_PAGE: Record<CustomPageType, string> = {
  faq: 'FREQUENTLY_ASKED_QUESTIONS',
  tos: 'TERMS_OF_SERVICE',
  pp: 'PRIVACY_POLICY',
};

interface CustomPageProps {
  pageType: CustomPageType;
  pageName: string;
}

const PAGE_TITLE_MAP: Record<CustomPageType, string> = {
  tos: PAGE_TITLE_TERMS,
  pp: PAGE_TITLE_PRIVACY_POLICY,
  faq: PAGE_TITLE_FAQ,
};

const CustomPage: React.FC<CustomPageProps> = ({ pageType, pageName }) => {
  const manifest = useSelector(selectManifest);
  const template = useSelector(selectTemplate);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const displayName = PAGE_TITLE_MAP[pageType];
    document.title = template?.title ? `${displayName} - ${template.title}` : displayName;
  }, [template, pageType]);

  useEffect(() => {
    awsRum?.recordEvent('PageView', { page: pageName });
  }, [pageName]);

  useEffect(() => {
    const controller = new AbortController();
    const manifestKey = CUSTOM_PAGE_MANIFEST_KEY[pageType];
    let pageUrl: string | undefined;

    const isPreview = getIsPreview();
    if (isPreview) {
      const linkType = FOOTER_LINK_TYPE_FOR_PAGE[pageType];
      const footerLink = (template?.footerLinks as ServiceFooterLink[])?.find(
        (link) => link.type === linkType && link.useCustomPage,
      );
      pageUrl = footerLink?.url;
    } else {
      pageUrl = manifest?.[manifestKey];
    }

    if (!pageUrl) {
      if (isPreview && !template) return;
      setLoading(false);
      setError(true);
      setContent(null);
      awsRum?.recordEvent('CustomPageError', { page: pageName, reason: 'no_manifest_url' });
      return;
    }

    const baseUrl = getBaseUrl();
    const fullUrl = pageUrl.startsWith('http') ? pageUrl : `${baseUrl}/${pageUrl}`;

    fetch(fullUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch custom page: ${response.status}`);
        }
        return response.text();
      })
      .then((markdown) => {
        setContent(markdown);
        setError(false);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error(`Failed to load ${pageName} page:`, err);
        awsRum?.recordEvent('CustomPageError', { page: pageName, error: err.message });
        setContent(null);
        setError(true);
        setLoading(false);
      });

    return () => controller.abort();
  }, [manifest, template, pageType, pageName]);

  if (loading) {
    return (
      <div className="customPage customPage--loading" data-testid="customPage">
        <LoadingSpinnerContainer>
          <LoadingSpinner />
        </LoadingSpinnerContainer>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="customPage customPage--error" data-testid="customPage">
        <ErrorBox heading={UH_OH} message={GENERIC_ERROR_STRING} metricEmitter={metricEmitter} />
      </div>
    );
  }

  return (
    <div data-testid="customPage">
      <CustomPageContent>
        <ReactMarkdown>{content}</ReactMarkdown>
      </CustomPageContent>
    </div>
  );
};

export default CustomPage;
